import { addDisplayName, addProps, flowMax } from 'ad-hok'
import { FC } from 'react'
import { createUseStyles } from 'react-jss'
import cx from 'classnames'
import { Trans } from 'react-i18next'
import { ProductWithImage } from 'shared/src/api/types'
import { addWindowSize } from 'shared/src/utils/addWindowSize'
import { flow, shuffle, take, uniqBy } from 'lodash/fp'

import { LAYOUT_MAX_WIDTH } from 'utils/layout'
import { serifFontFamily } from 'utils/styles'
import { LinkOrButton } from '../../LinkOrButton'
import { makeProductDetailUrl } from 'utils/routes'
import { addTranslationHelpers } from 'utils/addTranslationHelpers'

const WIDTH = 340
const HEIGHT = 579

const randomizeProducts: (features: ProductWithImage[]) => ProductWithImage[] =
  flow(shuffle, uniqBy('id'), take(4))

const useStyles = createUseStyles({
  container: {
    justifyContent: 'center',
    postion: 'relative',
    height: HEIGHT,
    width: WIDTH,
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: 16,
    paddingRight: 16,
    scale: ({
      windowWidth,
      windowHeight,
    }: {
      windowWidth: number
      windowHeight: number
    }) =>
      Math.min(
        Math.min(windowWidth - 32, LAYOUT_MAX_WIDTH - 32) / WIDTH,
        (windowHeight * 0.8) / HEIGHT
      ),
  },
  featuredText: {
    fontFamily: serifFontFamily,
    color: '#FFF',
    fontSize: 32,
    fontWeight: 700,
    lineHeight: '129.5%',
    letterSpacing: 0.32,
    marginLeft: 16,
    marginBottom: 19,
    position: 'absolute',
    top: 0,
    width: 166,
  },
  imagesContainer: {
    width: 340,
    height: 482,
    position: 'absolute',
    top: 90,
  },
  image: {
    position: 'absolute',
    objectFit: 'cover',
    width: 170,
  },
  image0: {
    top: 10,
    left: 0,
    height: 248,
  },
  image1: {
    right: 0,
    height: 226,
  },
  image2: {
    left: 0,
    bottom: 0,
    height: 222,
  },
  image3: {
    bottom: 0,
    right: 0,
    height: 268,
  },
  italics: {
    fontStyle: 'italic',
  },
})

interface Props {
  products: ProductWithImage[]
}

export const FeedFeaturedProduct: FC<Props> = flowMax(
  addDisplayName('FeedFeaturedProduct'),
  addProps(
    ({ products }) => ({
      products: randomizeProducts(products),
    }),
    ['products']
  ),
  addWindowSize,
  addTranslationHelpers,
  addProps(({ windowWidth, windowHeight }) => ({
    classes: useStyles({ windowWidth, windowHeight }),
  })),
  ({ products, classes, t }) => (
    <div className={classes.container}>
      <div className={classes.featuredText}>
        <Trans t={t} i18nKey={'feed.featuredProducts'}>
          <span className={classes.italics} />
        </Trans>
      </div>
      <div className={classes.imagesContainer}>
        {products.map(({ id, name, image }, index) => (
          <LinkOrButton
            navigateTo={makeProductDetailUrl(id)}
            showBackButton
            additionalState={{ discoverySource: 'feedFeature' }}
            className={cx(
              classes.image,
              [classes.image0, classes.image1, classes.image2, classes.image3][
                index
              ]
            )}
            key={index}
          >
            <img
              src={image.full}
              alt={name}
              className={cx(
                classes.image,
                [
                  classes.image0,
                  classes.image1,
                  classes.image2,
                  classes.image3,
                ][index]
              )}
            />
          </LinkOrButton>
        ))}
      </div>
    </div>
  )
)
